import type { MouseEvent } from 'react';
import { FavoriteToggle } from '@backstage/core-components';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import { makeStyles } from '@material-ui/core';
import { parseEntityRef } from '@backstage/catalog-model';
import type { ListRow } from './listFilters';

/**
 * The parts a row and a card share, so the table and the cards say the same
 * thing about an entry in the same words and the same colours.
 *
 * MUI v4 only — no MUI v5.
 */

const useStyles = makeStyles(theme => ({
  owner: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    minWidth: 0,
    overflowWrap: 'break-word',
  },
  initials: {
    flex: 'none',
    display: 'inline-grid',
    placeItems: 'center',
    minWidth: 20,
    height: 20,
    padding: '0 3px',
    borderRadius: 4,
    fontSize: '0.625rem',
    fontWeight: 700,
    letterSpacing: '0.02em',
    color: 'var(--ui-primary)',
    background: 'var(--ui-nav-active-bg)',
  },
  lifecycle: {
    display: 'inline-block',
    padding: '1px 6px',
    borderRadius: 4,
    border: '1px solid currentColor',
    fontSize: '0.75rem',
    lineHeight: 1.5,
    whiteSpace: 'nowrap',
    color: 'var(--ui-text-muted)',
  },
  production: { color: theme.palette.success.main },
  experimental: { color: theme.palette.warning.main },
  deprecated: { color: theme.palette.error.main },
  tags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  tag: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.7rem',
    lineHeight: 1.5,
    padding: '0 5px',
    borderRadius: 4,
    border: '1px solid var(--ui-border)',
    background: 'var(--ui-bg-muted)',
    color: 'var(--ui-text-muted)',
    whiteSpace: 'nowrap',
  },
  star: { padding: 4, marginLeft: -4 },
}));

/** Up to two letters for an owner: platform-engineering → PE. */
const initialsOf = (ref: string) => {
  let name = ref;
  try {
    name = parseEntityRef(ref).name;
  } catch {
    // A malformed owner still gets a badge from whatever it says.
  }
  return name
    .split(/[-_.\s]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]!.toLocaleUpperCase('en-US'))
    .join('');
};

export const OwnerOf = ({ row }: { row: ListRow }) => {
  const classes = useStyles();
  if (!row.owner) return <span className={classes.lifecycle}>No owner</span>;
  return (
    <span className={classes.owner}>
      <span className={classes.initials} aria-hidden>
        {initialsOf(row.owner)}
      </span>
      <EntityRefLink
        entityRef={row.owner}
        defaultKind="group"
        disableTooltip
        hideIcon
      />
    </span>
  );
};

export const LifecycleOf = ({ row }: { row: ListRow }) => {
  const classes = useStyles();
  if (!row.lifecycle) return null;
  const tone = row.lifecycle.toLocaleLowerCase('en-US');
  const toneClass =
    tone === 'production' || tone === 'experimental' || tone === 'deprecated'
      ? classes[tone]
      : undefined;
  return (
    <span className={`${classes.lifecycle} ${toneClass ?? ''}`}>
      {row.lifecycle}
    </span>
  );
};

/** How many tags a row shows before it says how many more there are. */
const TAGS_SHOWN = 3;

export const TagsOf = ({ row }: { row: ListRow }) => {
  const classes = useStyles();
  if (!row.tags.length) return null;
  const hidden = row.tags.length - TAGS_SHOWN;
  return (
    <ul className={classes.tags} aria-label="Tags">
      {row.tags.slice(0, TAGS_SHOWN).map(tag => (
        <li key={tag} className={classes.tag}>
          {tag}
        </li>
      ))}
      {hidden > 0 && (
        <li
          className={classes.tag}
          title={row.tags.slice(TAGS_SHOWN).join(', ')}
        >
          +{hidden}
        </li>
      )}
    </ul>
  );
};

/**
 * The reader's stars, handed down from the page rather than asked for by each
 * row: one subscription for the list, not one per entry.
 */
export type Stars = {
  isStarred: (ref: string) => boolean;
  toggle: (ref: string) => void;
};

/**
 * The star, the one Backstage keeps per reader — the same one an entity's
 * header carries and the sidebar lists. Named after the entry it stars, so a
 * screen reader moving down the list hears which one it would change.
 */
export const StarOf = ({ row, stars }: { row: ListRow; stars: Stars }) => {
  const classes = useStyles();
  const starred = stars.isStarred(row.ref);
  return (
    <FavoriteToggle
      id={`star-${row.ref}`}
      title={starred ? 'Remove from starred' : 'Add to starred'}
      aria-label={`Star ${row.title}`}
      aria-pressed={starred}
      isFavorite={starred}
      onToggle={() => stars.toggle(row.ref)}
      className={classes.star}
      size="small"
    />
  );
};

/**
 * A click on a row or a card, anywhere but on a control inside it.
 *
 * A click on a link or a button is that control's. A click with a modifier
 * held asks for what a link does with one — a tab of its own — so the entry
 * opens there and the list stays where it was; opening it here would throw
 * away the list and its filters, which is what the modifier was held to keep.
 */
export function clickOnEntry(e: MouseEvent, path: string, open: () => void) {
  if (e.target instanceof Element && e.target.closest('a, button')) return;
  if (e.metaKey || e.ctrlKey || e.shiftKey) {
    window.open(path, '_blank', 'noopener');
    return;
  }
  open();
}
